import {
  Component,
  ElementRef,
  EventEmitter, HostListener,
  Input, OnChanges, OnDestroy, OnInit,
  Output, SimpleChanges,
  ViewChild
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators
} from "@angular/forms";
import {Subscription} from "rxjs";
import {CommonService} from "../../../../services/common.service";
import {HeightFormatPipe} from "../../../../pipes/height-format.pipe";
import {DropdownOption} from "../../../../../../models/dropdown-option.model";

@Component({
  selector: 'app-height-input',
  templateUrl: './height-input.component.html',
  styleUrls: ['../../styles.css', './height-input.component.css']
})
export class HeightInputComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('input') input: ElementRef | undefined;

  @Input() label: string = '';
  @Input() focus: boolean = false;
  @Input() placeholder: string = '';
  @Input() parentFormGroup: FormGroup = new FormGroup<any>({});
  @Input() controlName: string = '';
  @Input() error: string = '';
  @Input() type: string = '';
  @Output() checkForErrors = new EventEmitter();

  fieldSubscription: Subscription | undefined;

  isRequired: boolean = false;
  isDisabled: boolean = false;

  form = new FormGroup({
    feet: new FormControl<number>(null),
    inches: new FormControl<number>(null),
    cm: new FormControl<number>(null),
  })
  formPlaceholders = {
    feet: "0'",
    inches: '00"',
    cm: '1/8'
  }

  cmOptions: DropdownOption[] = [
    {
      key: 0,
      value: '0',
    },
    {
      key: 1,
      value: '1/8',
    },
    {
      key: 2,
      value: '1/4',
    },
    {
      key: 3,
      value: '3/8',
    },
    {
      key: 4,
      value: '1/2',
    },
    {
      key: 5,
      value: '5/8',
    },
    {
      key: 6,
      value: '3/4',
    },
    {
      key: 7,
      value: '7/8',
    }
  ]

  skipValueChange = false;

  valueChangesSubscription: Subscription;

  constructor(
    private commonService: CommonService,
    private heightFormatPipe: HeightFormatPipe,
  ) {
    this.valueChangesSubscription = this.form.valueChanges.subscribe((values) => {
      if (this.skipValueChange) {
        this.skipValueChange = false;
        return;
      }
      const parentFormControl = this.parentFormGroup.controls[this.controlName];
      const feetInches = (+values.feet || 0) * 12;
      const inches = +values.inches || 0;
      const cmInches = (+values.cm || 0) / 8;

      const finalValueInInches = feetInches + inches + cmInches;

      this.skipValueChange =  true;
      if (finalValueInInches) {
        parentFormControl.patchValue('' + finalValueInInches);
      } else {
        parentFormControl.patchValue('');
      }
      parentFormControl.markAsTouched();
      parentFormControl.markAsDirty();
    })
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.['focus']?.currentValue) {
      const clientArea = $('#client_area');
      const y = clientArea.scrollTop() +
                this.input?.nativeElement.getBoundingClientRect().top -
                this.commonService.headerRemOffsetCalculator()

      clientArea.scrollTop(y);
    }
  }

  // Do not show field as touched and having error when clicked outside the field
  @HostListener('focusout', ['$event'])
  onBlur(event: Event) {
    if (!this.error) {
      this.parentFormGroup.controls[this.controlName].markAsUntouched();
    }
  }

  ngOnInit(): void {
    const control = this.parentFormGroup.controls[this.controlName];

    // set initial value to inputs
    this.setValueFromParentControl(control.value);

    this.isDisabled = control.status === 'DISABLED';
    if (this.isDisabled) {
      this.form.disable();
    }
    this.fieldSubscription = control.valueChanges.subscribe((value) => {
      // set value to inputs
      this.setValueFromParentControl(value);

      // If field is marked as touched, mark it as untouched
      if (this.parentFormGroup.controls[this.controlName].touched) {
        this.parentFormGroup.controls[this.controlName].markAsUntouched();
      }

      // Get errors on the form field
      const errors = this.parentFormGroup.controls[this.controlName].errors;

      // If errors exist, handle it
      if (errors) {
        // Clear errors on the form field
        this.parentFormGroup.controls[this.controlName].setErrors(null);

        // Clear out the error messages on the screen (parent component)
        this.checkForErrors.emit(this.controlName);

        // Add errors again to the form field for showing messages in the future
        this.parentFormGroup.controls[this.controlName].setErrors(errors);
      } else {
        // Clear out the error messages on the screen (parent component)
        this.checkForErrors.emit(this.controlName);
      }
    });

    // Check if field is required
    if (control.validator) {
      const validator = control.validator({} as AbstractControl);
      this.isRequired = !!(validator && validator['required']);
    }
  }

  setValueFromParentControl(value:any) {
    if (this.skipValueChange) {
      this.skipValueChange = false;
      return
    }
    if (value || value===0) {
      let parsedValue = this.heightFormatPipe.parse(+value);
      this.skipValueChange = true;
      this.form.patchValue(parsedValue);
    }
  }

  ngOnDestroy() {
    this.valueChangesSubscription?.unsubscribe();
    this.fieldSubscription?.unsubscribe();
  }
}
